import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  UserIcon,
  UserGroupIcon,
  CreditCardIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  CalendarIcon,


  ClockIcon
} from '@heroicons/react/24/outline';
import { tournamentsApi } from '../../services/api';
import LoadingSpinner from '../../components/LoadingSpinner';
import toast from 'react-hot-toast';

// Registration schemas
const individualRegistrationSchema = z.object({
  participant_name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Invalid email address'),
  phone: z.string().min(10, 'Phone number must be at least 10 digits'),
  age: z.number().min(5, 'Age must be at least 5').max(100, 'Age must be less than 100'),
  church_id: z.number().min(1, 'Please select a church'),
  emergency_contact: z.string().min(2, 'Emergency contact is required'),
  emergency_phone: z.string().min(10, 'Emergency phone is required'),
  medical_conditions: z.string().optional(),
  dietary_restrictions: z.string().optional(),
  consent_forms: z.boolean().refine(val => val === true, 'Consent forms must be accepted')
});

const teamRegistrationSchema = z.object({
  team_name: z.string().min(2, 'Team name must be at least 2 characters'),
  captain_name: z.string().min(2, 'Captain name is required'),
  captain_email: z.string().email('Invalid email address'),
  captain_phone: z.string().min(10, 'Phone number is required'),
  captain_age: z.number().min(5, 'Age must be at least 5').max(100, 'Age must be less than 100'),
  church_id: z.number().min(1, 'Please select a church'),
  members: z.array(z.object({
    name: z.string().min(2, 'Member name is required'),
    age: z.number().min(5, 'Age must be at least 5'),
    position: z.string().optional()
  })).min(1, 'At least one team member is required'),
  consent_forms: z.boolean().refine(val => val === true, 'Consent forms must be accepted')
});

type IndividualRegistration = z.infer<typeof individualRegistrationSchema>;
type TeamRegistration = z.infer<typeof teamRegistrationSchema>;

interface Tournament {
  id: number;
  name: string;
  description: string;
  competition_name: string;
  type: 'individual' | 'team';
  max_participants: number;
  registration_deadline: string;
  start_date: string;
  end_date: string;
  participant_count: number;
  registration_fee?: number;
  venue?: string;
  rules?: string;
  prizes?: string;
}

interface Church {
  id: number;
  name: string;
  location: string;
}

const TournamentRegistration: React.FC = () => {
  const { tournamentId } = useParams<{ tournamentId: string }>();
  const navigate = useNavigate();
  const [registrationType, setRegistrationType] = useState<'individual' | 'team'>('individual');
  const [currentStep, setCurrentStep] = useState(1);
  const [teamMembers, setTeamMembers] = useState([{ name: '', age: 0, position: '' }]);

  // Fetch tournament data
  const { data: tournament, isLoading: tournamentLoading } = useQuery<Tournament>(
    ['public-tournament', tournamentId],
    () => tournamentsApi.getById(parseInt(tournamentId!)),
    { enabled: !!tournamentId }
  );

  // Fetch churches
  const { data: churches = [], isLoading: churchesLoading } = useQuery<Church[]>(
    'churches',
    () => tournamentsApi.getChurches()
  );

  // Individual registration form
  const individualForm = useForm<IndividualRegistration>({
    resolver: zodResolver(individualRegistrationSchema),
    defaultValues: {
      consent_forms: false
    }
  });

  // Team registration form
  const teamForm = useForm<TeamRegistration>({
    resolver: zodResolver(teamRegistrationSchema),
    defaultValues: {
      members: [{ name: '', age: 0, position: '' }],
      consent_forms: false
    }
  });

  // Registration mutation
  const registerMutation = useMutation(
    (data: IndividualRegistration | TeamRegistration) =>
      tournamentsApi.register(parseInt(tournamentId!), {
        type: registrationType,
        ...data
      }),
    {
      onSuccess: () => {
        toast.success('Registration submitted successfully!');
        setCurrentStep(4); // Success step
      },
      onError: (error: any) => {
        toast.error((error as any)?.response?.data?.message || 'Registration failed');
      }
    }
  );

  useEffect(() => {
    if (tournament) {
      setRegistrationType(tournament.type);
    }
  }, [tournament]);

  const addTeamMember = () => {
    setTeamMembers([...teamMembers, { name: '', age: 0, position: '' }]);
  };

  const removeTeamMember = (index: number) => {
    if (teamMembers.length > 1) {
      setTeamMembers(teamMembers.filter((_, i) => i !== index));
    }
  };

  const handleIndividualSubmit = (data: IndividualRegistration) => {
    registerMutation.mutate(data);
  };

  const handleTeamSubmit = (data: TeamRegistration) => {
    registerMutation.mutate({ ...data, members: teamMembers });
  };

  if (tournamentLoading || churchesLoading) {
    return <LoadingSpinner />;
  }

  if (!tournament) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Tournament not found</h2>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  const isRegistrationClosed = new Date() > new Date(tournament.registration_deadline);
  const isFull = tournament.participant_count >= tournament.max_participants;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900">{tournament.name}</h1>
            <p className="mt-2 text-lg text-gray-600">{tournament.competition_name}</p>
            
            {/* Tournament Info */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="flex items-center justify-center space-x-2 text-sm text-gray-600">
                <CalendarIcon className="h-4 w-4" />
                <span>{new Date(tournament.start_date).toLocaleDateString()}</span>
              </div>
              <div className="flex items-center justify-center space-x-2 text-sm text-gray-600">
                <UserGroupIcon className="h-4 w-4" />
                <span>{tournament.participant_count}/{tournament.max_participants} registered</span>
              </div>
              <div className="flex items-center justify-center space-x-2 text-sm text-gray-600">
                <ClockIcon className="h-4 w-4" />
                <span>Deadline: {new Date(tournament.registration_deadline).toLocaleDateString()}</span>
              </div>
              {tournament.registration_fee && (
                <div className="flex items-center justify-center space-x-2 text-sm text-gray-600">
                  <CreditCardIcon className="h-4 w-4" />
                  <span>${tournament.registration_fee}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Registration Status */}
      {(isRegistrationClosed || isFull) && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className={`p-4 rounded-lg ${
            isRegistrationClosed ? 'bg-red-50 border border-red-200' : 'bg-yellow-50 border border-yellow-200'
          }`}>
            <div className="flex items-center space-x-2">
              <ExclamationTriangleIcon className={`h-5 w-5 ${
                isRegistrationClosed ? 'text-red-500' : 'text-yellow-500'
              }`} />
              <p className={`font-medium ${
                isRegistrationClosed ? 'text-red-800' : 'text-yellow-800'
              }`}>
                {isRegistrationClosed 
                  ? 'Registration has closed for this tournament'
                  : 'Tournament is full - registration closed'
                }
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Registration Form */}
      {!isRegistrationClosed && !isFull && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {currentStep === 4 ? (
            // Success Step
            <div className="bg-white rounded-lg shadow-sm p-8 text-center">
              <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Registration Successful!</h2>
              <p className="text-gray-600 mb-6">
                Your registration has been submitted and is pending approval. 
                You will receive a confirmation email shortly.
              </p>
              <button
                onClick={() => navigate('/')}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Back to Home
              </button>
            </div>
          ) : (
            // Registration Form Steps
            <div className="bg-white rounded-lg shadow-sm">
              {/* Step Indicator */}
              <div className="px-8 py-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  {[1, 2, 3].map((step) => (
                    <div key={step} className="flex items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                        currentStep >= step 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-gray-200 text-gray-600'
                      }`}>
                        {step}
                      </div>
                      {step < 3 && (
                        <div className={`w-16 h-1 mx-4 ${
                          currentStep > step ? 'bg-blue-600' : 'bg-gray-200'
                        }`} />
                      )}
                    </div>
                  ))}
                </div>
                <div className="mt-4 text-center">
                  <h3 className="text-lg font-medium text-gray-900">
                    {currentStep === 1 && 'Registration Type'}
                    {currentStep === 2 && 'Registration Details'}
                    {currentStep === 3 && 'Review & Submit'}
                  </h3>
                </div>
              </div>

              {/* Form Content */}
              <div className="p-8">
                {currentStep === 1 && (
                  <div className="space-y-6">
                    <h4 className="text-lg font-medium text-gray-900">Choose Registration Type</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <button
                        onClick={() => setRegistrationType('individual')}
                        className={`p-6 rounded-lg border-2 text-left ${
                          registrationType === 'individual'
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <UserIcon className="h-8 w-8 text-blue-600 mb-3" />
                        <h5 className="font-medium text-gray-900">Individual</h5>
                        <p className="text-sm text-gray-600 mt-1">
                          Register as an individual participant
                        </p>
                      </button>
                      
                      <button
                        onClick={() => setRegistrationType('team')}
                        className={`p-6 rounded-lg border-2 text-left ${
                          registrationType === 'team'
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <UserGroupIcon className="h-8 w-8 text-blue-600 mb-3" />
                        <h5 className="font-medium text-gray-900">Team</h5>
                        <p className="text-sm text-gray-600 mt-1">
                          Register as a team with multiple members
                        </p>
                      </button>
                    </div>
                    
                    <div className="flex justify-end">
                      <button
                        onClick={() => setCurrentStep(2)}
                        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      >
                        Continue
                      </button>
                    </div>
                  </div>
                )}

                {currentStep === 2 && (
                  <div>
                    {registrationType === 'individual' ? (
                      <form onSubmit={individualForm.handleSubmit(handleIndividualSubmit)} className="space-y-6">
                        <h4 className="text-lg font-medium text-gray-900">Individual Registration</h4>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {/* Participant Name */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Full Name *
                            </label>
                            <input
                              type="text"
                              {...individualForm.register('participant_name')}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Enter full name"
                            />
                            {individualForm.formState.errors.participant_name && (
                              <p className="mt-1 text-sm text-red-600">
                                {individualForm.formState.errors.participant_name.message}
                              </p>
                            )}
                          </div>

                          {/* Email */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Email Address *
                            </label>
                            <input
                              type="email"
                              {...individualForm.register('email')}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Enter email address"
                            />
                            {individualForm.formState.errors.email && (
                              <p className="mt-1 text-sm text-red-600">
                                {individualForm.formState.errors.email.message}
                              </p>
                            )}
                          </div>

                          {/* Phone */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Phone Number *
                            </label>
                            <input
                              type="tel"
                              {...individualForm.register('phone')}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Enter phone number"
                            />
                            {individualForm.formState.errors.phone && (
                              <p className="mt-1 text-sm text-red-600">
                                {individualForm.formState.errors.phone.message}
                              </p>
                            )}
                          </div>

                          {/* Age */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Age *
                            </label>
                            <input
                              type="number"
                              {...individualForm.register('age', { valueAsNumber: true })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Enter age"
                              min="5"
                              max="100"
                            />
                            {individualForm.formState.errors.age && (
                              <p className="mt-1 text-sm text-red-600">
                                {individualForm.formState.errors.age.message}
                              </p>
                            )}
                          </div>

                          {/* Church Selection */}
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Church *
                            </label>
                            <select
                              {...individualForm.register('church_id', { valueAsNumber: true })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="">Select your church</option>
                              {churches.map((church: any) => (
                                <option key={church.id} value={church.id}>
                                  {church.name} - {church.location}
                                </option>
                              ))}
                            </select>
                            {individualForm.formState.errors.church_id && (
                              <p className="mt-1 text-sm text-red-600">
                                {individualForm.formState.errors.church_id.message}
                              </p>
                            )}
                          </div>

                          {/* Emergency Contact */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Emergency Contact Name *
                            </label>
                            <input
                              type="text"
                              {...individualForm.register('emergency_contact')}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Emergency contact name"
                            />
                            {individualForm.formState.errors.emergency_contact && (
                              <p className="mt-1 text-sm text-red-600">
                                {individualForm.formState.errors.emergency_contact.message}
                              </p>
                            )}
                          </div>

                          {/* Emergency Phone */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Emergency Contact Phone *
                            </label>
                            <input
                              type="tel"
                              {...individualForm.register('emergency_phone')}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Emergency contact phone"
                            />
                            {individualForm.formState.errors.emergency_phone && (
                              <p className="mt-1 text-sm text-red-600">
                                {individualForm.formState.errors.emergency_phone.message}
                              </p>
                            )}
                          </div>

                          {/* Medical Conditions */}
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Medical Conditions (Optional)
                            </label>
                            <textarea
                              {...individualForm.register('medical_conditions')}
                              rows={3}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Any medical conditions or allergies we should know about"
                            />
                          </div>

                          {/* Dietary Restrictions */}
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Dietary Restrictions (Optional)
                            </label>
                            <textarea
                              {...individualForm.register('dietary_restrictions')}
                              rows={2}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Any dietary restrictions or food allergies"
                            />
                          </div>

                          {/* Consent Forms */}
                          <div className="md:col-span-2">
                            <div className="flex items-start">
                              <input
                                type="checkbox"
                                {...individualForm.register('consent_forms')}
                                className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <label className="ml-2 text-sm text-gray-700">
                                I agree to the terms and conditions, waiver of liability, and consent to participate in the tournament. I understand that participation involves physical activity and potential risks. *
                              </label>
                            </div>
                            {individualForm.formState.errors.consent_forms && (
                              <p className="mt-1 text-sm text-red-600">
                                {individualForm.formState.errors.consent_forms.message}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="flex justify-between">
                          <button
                            type="button"
                            onClick={() => setCurrentStep(1)}
                            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                          >
                            Back
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              individualForm.trigger().then((isValid) => {
                                if (isValid) {
                                  setCurrentStep(3);
                                }
                              });
                            }}
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                          >
                            Review
                          </button>
                        </div>
                      </form>
                    ) : (
                      <form onSubmit={teamForm.handleSubmit(handleTeamSubmit)} className="space-y-6">
                        <h4 className="text-lg font-medium text-gray-900">Team Registration</h4>

                        <div className="space-y-6">
                          {/* Team Information */}
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <h5 className="text-md font-medium text-gray-900 mb-4">Team Information</h5>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Team Name *
                                </label>
                                <input
                                  type="text"
                                  {...teamForm.register('team_name')}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  placeholder="Enter team name"
                                />
                                {teamForm.formState.errors.team_name && (
                                  <p className="mt-1 text-sm text-red-600">
                                    {teamForm.formState.errors.team_name.message}
                                  </p>
                                )}
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Church *
                                </label>
                                <select
                                  {...teamForm.register('church_id', { valueAsNumber: true })}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                >
                                  <option value="">Select church</option>
                                  {churches.map((church: any) => (
                                    <option key={church.id} value={church.id}>
                                      {church.name} - {church.location}
                                    </option>
                                  ))}
                                </select>
                                {teamForm.formState.errors.church_id && (
                                  <p className="mt-1 text-sm text-red-600">
                                    {teamForm.formState.errors.church_id.message}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Team Captain */}
                          <div className="bg-blue-50 p-4 rounded-lg">
                            <h5 className="text-md font-medium text-gray-900 mb-4">Team Captain Information</h5>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Captain Name *
                                </label>
                                <input
                                  type="text"
                                  {...teamForm.register('captain_name')}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  placeholder="Team captain name"
                                />
                                {teamForm.formState.errors.captain_name && (
                                  <p className="mt-1 text-sm text-red-600">
                                    {teamForm.formState.errors.captain_name.message}
                                  </p>
                                )}
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Captain Email *
                                </label>
                                <input
                                  type="email"
                                  {...teamForm.register('captain_email')}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  placeholder="Captain email address"
                                />
                                {teamForm.formState.errors.captain_email && (
                                  <p className="mt-1 text-sm text-red-600">
                                    {teamForm.formState.errors.captain_email.message}
                                  </p>
                                )}
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Captain Phone *
                                </label>
                                <input
                                  type="tel"
                                  {...teamForm.register('captain_phone')}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  placeholder="Captain phone number"
                                />
                                {teamForm.formState.errors.captain_phone && (
                                  <p className="mt-1 text-sm text-red-600">
                                    {teamForm.formState.errors.captain_phone.message}
                                  </p>
                                )}
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Captain Age *
                                </label>
                                <input
                                  type="number"
                                  {...teamForm.register('captain_age', { valueAsNumber: true })}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  placeholder="Captain age"
                                  min="5"
                                  max="100"
                                />
                                {teamForm.formState.errors.captain_age && (
                                  <p className="mt-1 text-sm text-red-600">
                                    {teamForm.formState.errors.captain_age.message}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Team Members */}
                          <div>
                            <div className="flex justify-between items-center mb-4">
                              <h5 className="text-md font-medium text-gray-900">Team Members</h5>
                              <button
                                type="button"
                                onClick={addTeamMember}
                                className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                              >
                                Add Member
                              </button>
                            </div>

                            {teamMembers.map((member, index) => (
                              <div key={index} className="bg-gray-50 p-4 rounded-lg mb-4">
                                <div className="flex justify-between items-center mb-3">
                                  <h6 className="text-sm font-medium text-gray-700">Member {index + 1}</h6>
                                  {teamMembers.length > 1 && (
                                    <button
                                      type="button"
                                      onClick={() => removeTeamMember(index)}
                                      className="text-red-600 hover:text-red-800 text-sm"
                                    >
                                      Remove
                                    </button>
                                  )}
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Name *
                                    </label>
                                    <input
                                      type="text"
                                      value={member.name}
                                      onChange={(e) => {
                                        const newMembers = [...teamMembers];
                                        newMembers[index].name = e.target.value;
                                        setTeamMembers(newMembers);
                                      }}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                      placeholder="Member name"
                                    />
                                  </div>

                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Age *
                                    </label>
                                    <input
                                      type="number"
                                      value={member.age || ''}
                                      onChange={(e) => {
                                        const newMembers = [...teamMembers];
                                        newMembers[index].age = parseInt(e.target.value) || 0;
                                        setTeamMembers(newMembers);
                                      }}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                      placeholder="Age"
                                      min="5"
                                      max="100"
                                    />
                                  </div>

                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Position
                                    </label>
                                    <input
                                      type="text"
                                      value={member.position}
                                      onChange={(e) => {
                                        const newMembers = [...teamMembers];
                                        newMembers[index].position = e.target.value;
                                        setTeamMembers(newMembers);
                                      }}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                      placeholder="Position/Role"
                                    />
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>

                          {/* Consent Forms */}
                          <div>
                            <div className="flex items-start">
                              <input
                                type="checkbox"
                                {...teamForm.register('consent_forms')}
                                className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <label className="ml-2 text-sm text-gray-700">
                                I agree to the terms and conditions, waiver of liability, and consent for all team members to participate in the tournament. I understand that participation involves physical activity and potential risks. *
                              </label>
                            </div>
                            {teamForm.formState.errors.consent_forms && (
                              <p className="mt-1 text-sm text-red-600">
                                {teamForm.formState.errors.consent_forms.message}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="flex justify-between">
                          <button
                            type="button"
                            onClick={() => setCurrentStep(1)}
                            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                          >
                            Back
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              teamForm.trigger().then((isValid) => {
                                if (isValid) {
                                  setCurrentStep(3);
                                }
                              });
                            }}
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                          >
                            Review
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                )}

                {currentStep === 3 && (
                  <div className="space-y-6">
                    <h4 className="text-lg font-medium text-gray-900">Review & Submit</h4>
                    <p className="text-gray-600">Please review your registration details before submitting.</p>

                    {registrationType === 'individual' ? (
                      <div className="bg-gray-50 p-6 rounded-lg">
                        <h5 className="text-md font-semibold text-gray-900 mb-4">Individual Registration Summary</h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <span className="text-sm font-medium text-gray-700">Name:</span>
                            <p className="text-gray-900">{individualForm.watch('participant_name') || 'Not provided'}</p>
                          </div>
                          <div>
                            <span className="text-sm font-medium text-gray-700">Email:</span>
                            <p className="text-gray-900">{individualForm.watch('email') || 'Not provided'}</p>
                          </div>
                          <div>
                            <span className="text-sm font-medium text-gray-700">Phone:</span>
                            <p className="text-gray-900">{individualForm.watch('phone') || 'Not provided'}</p>
                          </div>
                          <div>
                            <span className="text-sm font-medium text-gray-700">Age:</span>
                            <p className="text-gray-900">{individualForm.watch('age') || 'Not provided'}</p>
                          </div>
                          <div>
                            <span className="text-sm font-medium text-gray-700">Church:</span>
                            <p className="text-gray-900">
                              {churches.find((c: any) => c.id === individualForm.watch('church_id'))?.name || 'Not selected'}
                            </p>
                          </div>
                          <div>
                            <span className="text-sm font-medium text-gray-700">Emergency Contact:</span>
                            <p className="text-gray-900">{individualForm.watch('emergency_contact') || 'Not provided'}</p>
                          </div>
                          <div>
                            <span className="text-sm font-medium text-gray-700">Emergency Phone:</span>
                            <p className="text-gray-900">{individualForm.watch('emergency_phone') || 'Not provided'}</p>
                          </div>
                          <div>
                            <span className="text-sm font-medium text-gray-700">Consent:</span>
                            <p className="text-gray-900">
                              {individualForm.watch('consent_forms') ? '✅ Agreed' : '❌ Not agreed'}
                            </p>
                          </div>
                          {individualForm.watch('medical_conditions') && (
                            <div className="md:col-span-2">
                              <span className="text-sm font-medium text-gray-700">Medical Conditions:</span>
                              <p className="text-gray-900">{individualForm.watch('medical_conditions')}</p>
                            </div>
                          )}
                          {individualForm.watch('dietary_restrictions') && (
                            <div className="md:col-span-2">
                              <span className="text-sm font-medium text-gray-700">Dietary Restrictions:</span>
                              <p className="text-gray-900">{individualForm.watch('dietary_restrictions')}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="bg-gray-50 p-6 rounded-lg">
                        <h5 className="text-md font-semibold text-gray-900 mb-4">Team Registration Summary</h5>

                        {/* Team Information */}
                        <div className="mb-6">
                          <h6 className="text-sm font-semibold text-gray-800 mb-2">Team Information</h6>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <span className="text-sm font-medium text-gray-700">Team Name:</span>
                              <p className="text-gray-900">{teamForm.watch('team_name') || 'Not provided'}</p>
                            </div>
                            <div>
                              <span className="text-sm font-medium text-gray-700">Church:</span>
                              <p className="text-gray-900">
                                {churches.find((c: any) => c.id === teamForm.watch('church_id'))?.name || 'Not selected'}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Team Captain */}
                        <div className="mb-6">
                          <h6 className="text-sm font-semibold text-gray-800 mb-2">Team Captain</h6>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <span className="text-sm font-medium text-gray-700">Name:</span>
                              <p className="text-gray-900">{teamForm.watch('captain_name') || 'Not provided'}</p>
                            </div>
                            <div>
                              <span className="text-sm font-medium text-gray-700">Email:</span>
                              <p className="text-gray-900">{teamForm.watch('captain_email') || 'Not provided'}</p>
                            </div>
                            <div>
                              <span className="text-sm font-medium text-gray-700">Phone:</span>
                              <p className="text-gray-900">{teamForm.watch('captain_phone') || 'Not provided'}</p>
                            </div>
                            <div>
                              <span className="text-sm font-medium text-gray-700">Age:</span>
                              <p className="text-gray-900">{teamForm.watch('captain_age') || 'Not provided'}</p>
                            </div>
                          </div>
                        </div>

                        {/* Team Members */}
                        <div className="mb-6">
                          <h6 className="text-sm font-semibold text-gray-800 mb-2">Team Members ({teamMembers.length})</h6>
                          <div className="space-y-2">
                            {teamMembers.map((member, index) => (
                              <div key={index} className="flex justify-between items-center bg-white p-3 rounded border">
                                <div className="flex space-x-4">
                                  <span className="font-medium">{member.name || `Member ${index + 1}`}</span>
                                  <span className="text-gray-600">Age: {member.age || 'N/A'}</span>
                                  {member.position && <span className="text-gray-600">Position: {member.position}</span>}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Consent */}
                        <div>
                          <span className="text-sm font-medium text-gray-700">Team Consent:</span>
                          <p className="text-gray-900">
                            {teamForm.watch('consent_forms') ? '✅ Agreed to terms for all team members' : '❌ Not agreed'}
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="flex justify-between">
                      <button
                        onClick={() => setCurrentStep(2)}
                        className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                      >
                        Back to Edit
                      </button>
                      <button
                        onClick={() => {
                          if (registrationType === 'individual') {
                            individualForm.handleSubmit(handleIndividualSubmit)();
                          } else {
                            teamForm.handleSubmit(handleTeamSubmit)();
                          }
                        }}
                        disabled={registerMutation.isLoading}
                        className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center space-x-2"
                      >
                        {registerMutation.isLoading && (
                          <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        )}
                        <span>{registerMutation.isLoading ? 'Submitting...' : 'Submit Registration'}</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TournamentRegistration;
